import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/cn';
import { statusLabel } from '@/lib/format';
import type { ProgressStatus, PublicationStatus } from '@/types/api';

type Tone = 'tech' | 'ai' | 'agent' | 'neutral' | 'ok' | 'warn' | 'danger';

const TONES: Record<Tone, string> = {
  tech: 'bg-tech/12 text-tech ring-tech/25',
  ai: 'bg-ai/12 text-ai ring-ai/25',
  agent: 'bg-agent/12 text-agent ring-agent/25',
  neutral: 'bg-surface-2 text-muted ring-border',
  ok: 'bg-ok/12 text-ok ring-ok/25',
  warn: 'bg-warn/12 text-warn ring-warn/25',
  danger: 'bg-danger/12 text-danger ring-danger/25',
};

export function Badge({
  className,
  tone = 'neutral',
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: Tone }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-2xs font-semibold',
        'ring-1 ring-inset',
        TONES[tone],
        className,
      )}
      {...props}
    />
  );
}

const PUBLICATION_TONES: Record<PublicationStatus, Tone> = {
  published: 'ok',
  in_preparation: 'warn',
  draft: 'neutral',
  archived: 'neutral',
};

export function PublicationBadge({ status }: { status: PublicationStatus }) {
  return <Badge tone={PUBLICATION_TONES[status]}>{statusLabel(status)}</Badge>;
}

const PROGRESS_TONES: Record<ProgressStatus, Tone> = {
  completed: 'ok',
  in_progress: 'tech',
  not_started: 'neutral',
};

export function ProgressBadge({ status }: { status: ProgressStatus }) {
  return <Badge tone={PROGRESS_TONES[status]}>{statusLabel(status)}</Badge>;
}

export function TrackBadge({ code }: { code: string }) {
  const tone: Tone = code === 'A' ? 'tech' : code === 'B' ? 'ai' : 'agent';
  return <Badge tone={tone}>Parcours {code}</Badge>;
}
